/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';
import { requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { getAxios } from '@/lib/axios';
import {
  type AutoCompleteSuggestionsResponse,
  type SuggestionsData,
  type SuggestionsOptions,
} from '../interface';
import { type AxiosInstance } from 'axios';
import { useLocale } from '@/hooks/useLocale';
import type { Locale } from '@/types/i18n';
import { createQuery } from '@/utils/createReactQuery';
import { getCountryFromLocale } from '@/utils/getCountryFromLocale';
import { type GetAcesAutoSuggestUsingGetParamsModel } from '@/api/types/autocomplete-types';
import { showXMPreviewDate } from '@/utils/showXMPreviewDate';
import { keepPreviousData } from '@tanstack/react-query';
import {
  getExperimentId,
  getSearchExperimentConfig,
  buildSearchExperimentHeaders,
  buildSearchExperimentParams,
} from '@/utils/searchExperiment';
import { validateAndFilterVehicleParams } from '@/features/ymme/utils/validateVehicleParams';
import { useRetryConfigForCategory } from '@/components/RetryConfigContext';
import { useVertexPersonalizationParams } from '../hooks/useVertexPersonalizationParams';
import { getSHA256 } from '@/utils/getSHA256';
import { useExperimentId } from '@/utils/searchExperiment/useExperimentId';
import environmentConfig from '@/config/environment';

const URL = `${requestBaseURLGCPExtOrInt}/sls/pd/product-browse-search/v1/auto-suggestions`;

type Options = {
  locale: Locale;
  preview: boolean;
  searchTerm?: string;
  year?: number;
  makeId?: number;
  modelId?: number;
  vehicleTypeId?: number;
  vehicleQuestions?: string | undefined;
  storeId?: number;
  experimentId?: string;
  visitorId?: string;
  userId?: string;
};

const getSuggestions = async (options: Options, axiosInstance?: AxiosInstance) => {
  const {
    locale,
    preview,
    searchTerm,
    year,
    makeId,
    modelId,
    vehicleTypeId,
    vehicleQuestions,
    storeId,
    experimentId,
  } = options;
  const country = getCountryFromLocale(locale);
  const customerType = 'B2C';
  const salesChannel = 'ECOMM';

  // Validate vehicle parameters - if any base param is missing, exclude ALL vehicle params
  const validatedVehicleParams = validateAndFilterVehicleParams({
    year,
    makeId,
    modelId,
    vehicleTypeId,
    vehicleQuestions,
  });

  const normalizedExperimentId = getExperimentId(experimentId, true);
  const searchExpConfig = getSearchExperimentConfig({
    experimentId: normalizedExperimentId,
    experimentEnabled: true, // Search experiment is always enabled for suggestions
  });

  const params: GetAcesAutoSuggestUsingGetParamsModel = {
    country,
    customerType,
    preview,
    salesChannel,
    ...(searchTerm ? { searchTerm } : { searchTerm: '' }),
    storeId,
    ...validatedVehicleParams,
  };

  // Hash userId if provided (for both client and server-side calls)
  const hashedUserId = options.userId ? await getSHA256(options.userId) : '';
  const enhancedParams = buildSearchExperimentParams(searchExpConfig, params, {
    visitorId: options.visitorId,
    userId: hashedUserId,
  });
  const headers = buildSearchExperimentHeaders(searchExpConfig);

  const response = await getAxios(axiosInstance).get<AutoCompleteSuggestionsResponse>(URL, {
    ...(headers && { headers }),
    params: enhancedParams,
    withCredentials: !environmentConfig.DISABLE_GCP_API_CREDENTIALS,
  });
  return newSelector(response.data);
};

const newSelector = (data: AutoCompleteSuggestionsResponse): SuggestionsData => {
  return {
    generalSuggestions: data.generalSuggestions ?? [],
    productTitleSuggestions: data.productTitleSuggestions ?? [],
  };
};

const { useData: useSearchSuggestions } = createQuery<SuggestionsData, Options>(
  'searchSuggestions',
  getSuggestions,
);

export const useSuggestionsACES = ({
  enabled = true,
  ...options
}: SuggestionsOptions & { enabled?: boolean }) => {
  const locale = useLocale();
  const experimentId = useExperimentId();
  const vertexParams = useVertexPersonalizationParams();
  const retryConfig = useRetryConfigForCategory('SEARCH');

  return useSearchSuggestions({
    enabled: enabled && vertexParams.isMyProfileReady,
    locale,
    placeholderData: keepPreviousData,
    ...options,
    preview: showXMPreviewDate(),
    ...(experimentId && { experimentId }),
    ...(vertexParams.shouldInjectParams && {
      visitorId: vertexParams.visitorId,
      userId: vertexParams.userId,
    }),
    ...retryConfig,
  });
};
